import { login } from '../api/admin'
import { handoffErrorReasonFromBody } from './qcsHandoffError'

export const HANDOFF_ERROR_MESSAGE_DEFAULT = 'Unable to complete supervision handoff. Please sign in or try again.'
export const HANDOFF_MFA_ERROR_MESSAGE =
  'This handoff requires an extra verification step that is not supported here. Please sign in with email and password.'

export const HANDOFF_RESULT = {
  SUCCESS: 'success',
  ERROR_PAGE: 'error-page',
  ERROR: 'error',
}

const handoffStorageKey = handoffId => `qcs-handoff-login:${handoffId}`

/** Survives Login remounts so the same handoff id shares one in-flight POST. */
const handoffInFlight = new Map()

export const isQcsHandoffDone = handoffId => {
  if (!handoffId) return false
  try {
    return sessionStorage.getItem(handoffStorageKey(handoffId)) === 'done'
  } catch (e) {
    return false
  }
}

const markHandoffDone = handoffId => {
  try {
    sessionStorage.setItem(handoffStorageKey(handoffId), 'done')
  } catch (e) {
    // ignore
  }
}

const clearHandoffAttempt = handoffId => {
  try {
    sessionStorage.removeItem(handoffStorageKey(handoffId))
  } catch (e) {
    // ignore
  }
}

const errorResult = message => ({ type: HANDOFF_RESULT.ERROR, message: message || HANDOFF_ERROR_MESSAGE_DEFAULT })

const toResult = (handoffId, response) => {
  if (response.ok) {
    return response.text().then(text => {
      if (text.includes('No MFA required')) {
        markHandoffDone(handoffId)
        return { type: HANDOFF_RESULT.SUCCESS }
      }
      // OTP needs a username; handoff must not open a broken OTP screen.
      clearHandoffAttempt(handoffId)
      return errorResult(HANDOFF_MFA_ERROR_MESSAGE)
    })
  }

  clearHandoffAttempt(handoffId)

  if (response.status === 400) {
    return response
      .json()
      .catch(() => null)
      .then(body => ({ type: HANDOFF_RESULT.ERROR_PAGE, reason: handoffErrorReasonFromBody(body) }))
  }

  if (response.status >= 400) {
    return response
      .json()
      .then(body => errorResult(body && body.message))
      .catch(() => errorResult())
  }

  return errorResult()
}

/**
 * POST /login with the QCS handoff id and resolve to one outcome:
 * SUCCESS, ERROR_PAGE (`reason` for the handoff error page) or ERROR (`message`).
 */
export const loginWithQcsHandoff = handoffId => {
  let request = handoffInFlight.get(handoffId)
  if (!request) {
    request = login({ qcsHandoffId: handoffId })
      .then(response => toResult(handoffId, response))
      .catch(() => {
        clearHandoffAttempt(handoffId)
        return errorResult()
      })
      .finally(() => {
        handoffInFlight.delete(handoffId)
      })
    handoffInFlight.set(handoffId, request)
  }
  return request
}
